import { useState, useMemo } from "react";
import { useStore } from "@/lib/store";
import { Eye, FileText, Copy, Check, ImageIcon, CheckCircle2, ArrowLeft } from "lucide-react";
import CarouselPreview from "./carousel-preview";
import CarouselBrandAssets from "./carousel-brand-assets";
import { parseCarouselResponse, type ParsedCarousel } from "./carousel-parser";
import { VP_CAROUSEL_STYLES } from "./carousel-styles";

type PanelView = "preview" | "text" | "assets";

export default function CarouselSidePanel() {
  const messages = useStore((s) => s.messages);
  const carouselStyleId = useStore((s) => s.carouselStyleId);
  const setCarouselStyleId = useStore((s) => s.setCarouselStyleId);
  const [view, setView] = useState<PanelView>("preview");
  const [copied, setCopied] = useState(false);

  const latestCarousel = useMemo((): { parsed: ParsedCarousel; rawText: string } | null => {
    const assistantMessages = messages.filter((m) => m.role === "assistant");
    for (let i = assistantMessages.length - 1; i >= 0; i--) {
      const parsed = parseCarouselResponse(assistantMessages[i].content);
      if (parsed) return { parsed, rawText: assistantMessages[i].content };
    }
    return null;
  }, [messages]);

  const handleCopy = () => {
    if (!latestCarousel) return;
    navigator.clipboard.writeText(latestCarousel.rawText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Brand assets tab is always accessible
  if (!latestCarousel && view !== "assets") {
    return (
      <div className="h-full flex flex-col">
        {/* Header with assets tab */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <span className="text-xs font-semibold text-muted-foreground">Styl karuzeli</span>
          <button
            onClick={() => setView("assets")}
            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-muted-foreground hover:text-foreground transition-all"
          >
            <ImageIcon size={11} />
            Assety
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <p className="text-muted-foreground text-xs mb-4">
            Wybierz styl przed generowaniem karuzeli
          </p>
          <div className="space-y-3">
            {VP_CAROUSEL_STYLES.map((style) => {
              const isActive = carouselStyleId === style.id;
              return (
                <button
                  key={style.id}
                  onClick={() => setCarouselStyleId(isActive ? null : style.id)}
                  className={`w-full text-left rounded-xl border p-4 transition-all ${
                    isActive
                      ? "border-red-500/60 bg-red-500/10"
                      : "border-border bg-muted/20 hover:border-border hover:bg-muted/40"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-semibold text-foreground">{style.name}</span>
                    {isActive && <CheckCircle2 size={14} className="text-red-500" />}
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
                    {style.description}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {style.bestFor.map((tag) => (
                      <span
                        key={tag}
                        className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  {/* Slide preview — framed so the intentionally dark background is clear */}
                  <div className="mt-3 rounded-lg overflow-hidden border border-border shadow-sm">
                    <div className="bg-muted/60 px-2 py-0.5 border-b border-border flex items-center gap-1">
                      <span className="text-[9px] text-muted-foreground uppercase tracking-wide">
                        Podgląd slajdu
                      </span>
                    </div>
                    <div
                      className="h-10 flex items-center justify-center gap-1 px-2"
                      style={{ backgroundColor: style.colors.bg }}
                    >
                      <span
                        style={{
                          color: style.colors.text,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.01em",
                        }}
                      >
                        Twój
                      </span>
                      <span
                        style={{
                          color: style.colors.accent,
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: "0.01em",
                        }}
                      >
                        nagłówek
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (view === "assets") {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <button
            onClick={() => setView("preview")}
            className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium text-muted-foreground hover:text-foreground transition-all"
          >
            <ArrowLeft size={11} />
            {latestCarousel ? "Podgląd" : "Styl karuzeli"}
          </button>
          <span className="text-xs font-semibold text-muted-foreground">Assety firmowe</span>
        </div>
        <div className="flex-1 overflow-hidden">
          <CarouselBrandAssets />
        </div>
      </div>
    );
  }

  // At this point: view is 'preview' | 'text' AND latestCarousel is non-null
  if (!latestCarousel) return null;

  return (
    <div className="h-full flex flex-col">
      {/* Panel header with view toggle */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
        <span className="text-xs font-semibold text-muted-foreground truncate">
          {latestCarousel?.parsed.carouselTitle ?? "Karuzela"}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setView("preview")}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-all ${
              view === "preview"
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Eye size={11} />
            Podgląd
          </button>
          <button
            onClick={() => setView("text")}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-all ${
              view === "text"
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <FileText size={11} />
            Tekst
          </button>
          <button
            onClick={() => setView("assets")}
            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-all text-muted-foreground hover:text-foreground"
          >
            <ImageIcon size={11} />
            Assety
          </button>
        </div>
      </div>

      {/* Panel body */}
      <div className="flex-1 overflow-y-auto">
        {view === "preview" ? (
          <CarouselPreview carousel={latestCarousel.parsed} />
        ) : (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-muted-foreground">Pełny output AI</span>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-all"
              >
                {copied ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
                {copied ? "Skopiowano" : "Kopiuj"}
              </button>
            </div>
            <div className="text-[13px] text-foreground/70 leading-relaxed whitespace-pre-wrap bg-muted/30 rounded-lg p-4 border border-border">
              {latestCarousel.rawText}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
